import React from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useFormikContext } from 'formik';
import { useAppDispatch } from 'store/hooks';
import { useParams } from 'react-router-dom';

import { reorderCourseLessons } from 'store/course/actions';

import { getTranslation } from 'helpers/getTranslation';

import { useModal } from 'hooks/useModal';

import { CopySettings } from '../CopySettings';

import Lessons from './Lessons';

type LessonSortable = {
  id: string;
  order?: number;
  [key: string]: unknown;
};

type LessonsContainerProps = {
  lessons: LessonSortable[];
  moduleId?: string;
  namePrefix?: string;
  courseReadOnly?: boolean;
  onEditLessonClick?: () => void;
  onRemoveLessonClick?: (lesson: LessonSortable) => void;
};

const LessonsContainer = ({
  lessons,
  moduleId,
  namePrefix,
  ...restProps
}: LessonsContainerProps) => {
  const dispatch = useAppDispatch();

  const { id: courseId } = useParams<{ id: string }>();

  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [
    copySettingsOpen,
    openCopySettings,
    closeCopySettings,
    copySettingsData,
  ] = useModal({
    defaultOpen: false,
  });

  const handleLessonDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = lessons.findIndex(
      (lesson: any) => lesson.id === active.id,
    );
    const newIndex = lessons.findIndex((lesson: any) => lesson.id === over.id);

    const nextLessons = arrayMove(lessons, oldIndex, newIndex).map(
      (lesson: any, lessonIndex: any) => ({
        ...lesson,
        order: lessonIndex + 1,
      }),
    );

    setFieldValue(
      namePrefix
        ? `${namePrefix}.lessons`
        : getTranslation('course_page.lessons.text'),
      nextLessons,
    );

    dispatch(
      reorderCourseLessons({
        courseId,
        data: {
          moduleId: moduleId,
          lessons: nextLessons.map((lesson: any) => lesson.id),
        },
      }),
    );
  };

  const handleCopyLessonClick = (event: React.MouseEvent, lessonId: string) => {
    event.stopPropagation();

    openCopySettings({ lessonId });
  };

  return (
    <>
      <Lessons
        {...restProps}
        structure={values.structure}
        lessons={lessons}
        sensors={sensors}
        onLessonDragEnd={handleLessonDragEnd}
        onCopyLessonClick={handleCopyLessonClick}
      />
      <CopySettings
        {...copySettingsData}
        open={copySettingsOpen}
        onClose={closeCopySettings}
      />
    </>
  );
};

export default React.memo(LessonsContainer);
